import Tippy from '@tippyjs/react';
import { Column } from 'react-table';

import { CustomHeader } from 'app/components/Table/columns/CustomHeader';
import Typography from 'app/components/Typography';

import { StatusStockUp } from 'app/modules/inventory/stock-run/core/enum';
import { getStockHealth } from 'app/modules/reporting/core/columns';

import { formatDate } from 'app/helpers';

import { ReactComponent as CommentIcon } from 'app/assets/icons/comment.svg';

import ItemInfoCard from '../../items/components/ItemInfoCard';
import SKUCard from '../../items/components/SKUCard';
import { getItemAttribute } from './helpers';
import { StockRunItem } from './type';

export const itemsStockRunColumns = (
  status: StatusStockUp | undefined,
  onOpenCommentModal: (itemName: string, comment: string) => () => void,
): ReadonlyArray<Column<StockRunItem>> => {
  const columns: Column<StockRunItem>[] = [
    {
      id: '#',
      accessor: 'id',
      Header: '#',
      width: '5%',
    },
    {
      id: 'name',
      accessor: 'inventory',
      Header: (props) => <CustomHeader tableProps={props} title="Item" />,
      Cell: ({ ...props }) => (
        <ItemInfoCard
          image={props.data[props.row.index].inventory?.image || ''}
          name={props.data[props.row.index].inventory?.name || ''}
          stockType={props.data[props.row.index].inventory?.stockType}
          stockupQuantity={Number(props.data[props.row.index].qtyCompleted)}
          isStockUpCompleted={props.data[props.row.index].isCompleted}
          isNotNavigatable={true}
          isPreview
        />
      ),

      width: '30%',
    },
    {
      id: 'comment',
      Header: <Typography className="opacity-0">Comment</Typography>,
      Cell: ({ ...props }) => {
        const itemName = props.data[props.row.index].inventory?.name || '';
        const comment = props.data[props.row.index].forceCompletedReason || '';

        return status !== StatusStockUp['Force-completed'] ? null : (
          <Tippy content="Click to view full comment" interactive theme="dark" placement="top">
            <div
              onClick={onOpenCommentModal(itemName, comment || '')}
              className="d-flex align-items-center w-20px h-20px cursor-pointer"
            >
              <CommentIcon className="w-100 h-100" />
            </div>
          </Tippy>
        );
      },
    },
    {
      id: 'sku',
      accessor: 'inventory',
      Header: (props) => <CustomHeader tableProps={props} title="SKU" />,
      Cell: ({ ...props }) => (
        <SKUCard
          skuId={props.data[props.row.index].inventory?.skuId || ''}
          skuSub={props.data[props.row.index].inventory?.skuSub || ''}
          stockRunQtyCompleted={Number(props.data[props.row.index].qtyCompleted)}
          isStockRunCompleted={props.data[props.row.index].isCompleted}
        />
      ),
      width: '15%',
    },
    {
      id: 'qtyUnit',
      accessor: 'qtyUnit',
      Header: (props) => <CustomHeader tableProps={props} title="Unit" />,
      Cell: ({ value }) => <Typography variant="body-5">{value?.name}</Typography>,
    },
    {
      id: 'stockLevel',
      width: '15%',
      Header: (props) => <CustomHeader tableProps={props} title="Stock health" />,
      Cell: ({ ...props }) => getStockHealth(props.data[props.row.index].stockLevel),
    },
    {
      id: 'rack',
      accessor: 'rack',
      Header: (props) => <CustomHeader tableProps={props} title="Rack" />,

      Cell: ({ ...props }) => (
        <Typography
          color={getItemAttribute('color', {
            isCompleted: props.data[props.row.index].isCompleted,
            qtyCompleted: props.data[props.row.index].qtyCompleted,
          })}
          variant="body-5"
          style={{
            whiteSpace: 'nowrap',
            textDecoration: getItemAttribute('textDecoration', {
              isCompleted: props.data[props.row.index].isCompleted,
              qtyCompleted: props.data[props.row.index].qtyCompleted,
            }),
          }}
        >
          {props.data[props.row.index].bin?.warehouse?.name}
        </Typography>
      ),
    },
    {
      id: 'bin',
      accessor: 'bin',
      Header: (props) => <CustomHeader tableProps={props} title="Bin" />,
      Cell: ({ ...props }) => (
        <Typography
          color={getItemAttribute('color', {
            isCompleted: props.data[props.row.index].isCompleted,
            qtyCompleted: props.data[props.row.index].qtyCompleted,
          })}
          variant="body-5"
          style={{
            textDecoration: getItemAttribute('textDecoration', {
              isCompleted: props.data[props.row.index].isCompleted,
              qtyCompleted: props.data[props.row.index].qtyCompleted,
            }),
          }}
        >
          {props.data[props.row.index].bin?.name}
        </Typography>
      ),
    },
    {
      id: 'quantity',
      accessor: 'quantity',
      Header: (props) => <CustomHeader tableProps={props} title="Qty to stock up" />,
      Cell: ({ ...props }) => (
        <Typography
          color={getItemAttribute('qtyColor', {
            isCompleted: props.data[props.row.index].isCompleted,
            qtyCompleted: props.data[props.row.index].qtyCompleted,
          })}
          variant="body-5"
        >
          {Number(props.data[props.row.index].qtyCompleted) === 0 &&
          props.data[props.row.index].isCompleted
            ? 'Removed'
            : props.data[props.row.index].quantity}
        </Typography>
      ),
    },
    {
      id: 'qtyCompleted',
      accessor: 'qtyCompleted',
      Header: (props) => <CustomHeader tableProps={props} title=" Qty completed" />,
      Cell: ({ ...props }) => (
        <Typography
          color={getItemAttribute('color', {
            isCompleted: props.data[props.row.index].isCompleted,
            qtyCompleted: props.data[props.row.index].qtyCompleted,
          })}
          variant="body-5"
        >
          {Number(props.data[props.row.index].qtyCompleted) === 0 &&
          props.data[props.row.index].isCompleted
            ? '-'
            : props.data[props.row.index].qtyCompleted}
        </Typography>
      ),
    },
    {
      id: 'lotNumber',
      accessor: 'lotNumber',
      Header: (props) => <CustomHeader tableProps={props} title="Lot number" />,
      Cell: ({ ...props }) => (
        <Typography variant="body-5">{props.data[props.row.index].expiry?.lotNumber}</Typography>
      ),
    },
    {
      id: 'expiryDate',
      Header: (props) => <CustomHeader tableProps={props} title="Expiry date" />,
      Cell: ({ ...props }) => {
        return (
          <Typography variant="body-5">
            {formatDate(props.data[props.row.index].expiry?.expiryDate || '')}
          </Typography>
        );
      },
    },
    {
      id: 'alertDate',
      Header: (props) => <CustomHeader tableProps={props} title="Alert date" />,
      Cell: ({ ...props }) => {
        return (
          <Typography variant="body-5">
            {formatDate(props.data[props.row.index].expiry?.alertDate || '')}
          </Typography>
        );
      },
    },
  ];

  return columns;
};
